import { IconBuildingStore } from "@tabler/icons-react";
import type { TFunction } from "i18next";
import { MarketplaceBadge } from "@/components/shared/marketplace-badge";
import type { ScopeSummary } from "./model";

/** Marketplace code chip ("US", "DE"); swaps to surface on highlighted rows so it stays visible. */
function CountPill({ count }: { count: number }) {
  return (
    <span className="inline-flex h-[18px] shrink-0 items-center rounded-full bg-subtle px-1.5 text-badge font-medium text-text-secondary tabular-nums">
      {count}
    </span>
  );
}

function StoreIcon() {
  return (
    <span className="flex size-4 shrink-0 items-center justify-center" aria-hidden>
      <IconBuildingStore className="size-[15px] text-text-secondary" stroke={1.75} />
    </span>
  );
}

/** Plain-text form of the summary for the trigger's accessible name and tooltip. */
export function summaryText(summary: ScopeSummary, t: TFunction<"dashboard">): string {
  switch (summary.kind) {
    case "all":
      return t("shopScope.allShops");
    case "account":
      return `${summary.name} · ${t("shopScope.shopCount", { count: summary.count })}`;
    case "shop":
      return summary.name;
    case "mix":
      return t("shopScope.shopCount", { count: summary.count });
    case "none":
      return t("shopScope.chooseShop");
  }
}

/** Visible trigger content for each Paper trigger variant. */
export function ScopeSummaryContent({
  summary,
  t,
}: {
  summary: ScopeSummary;
  t: TFunction<"dashboard">;
}) {
  switch (summary.kind) {
    case "all":
      return (
        <>
          <StoreIcon />
          <span className="truncate">{t("shopScope.allShops")}</span>
          <CountPill count={summary.count} />
        </>
      );
    case "account":
      return (
        <>
          <StoreIcon />
          <span className="truncate">{summary.name}</span>
          <CountPill count={summary.count} />
        </>
      );
    case "shop":
      return (
        <>
          {summary.marketplace ? <MarketplaceBadge code={summary.marketplace} /> : <StoreIcon />}
          <span className="truncate">{summary.name}</span>
        </>
      );
    case "mix":
      return (
        <>
          {summary.marketplaces.length > 0 ? (
            <span className="flex shrink-0 items-center gap-0.5">
              {summary.marketplaces.map((code) => (
                <MarketplaceBadge key={code} code={code} />
              ))}
            </span>
          ) : (
            <StoreIcon />
          )}
          <span className="truncate tabular-nums">
            {t("shopScope.shopCount", { count: summary.count })}
          </span>
        </>
      );
    case "none":
      return (
        <>
          <StoreIcon />
          <span className="truncate text-placeholder">{t("shopScope.chooseShop")}</span>
        </>
      );
  }
}
